import { Button } from '@thanx/uikit/button'
import { Switch } from '@thanx/uikit/switch'
import { Text } from '@thanx/uikit/text'
import { useGetPromotionsQuery } from 'api/promotion'
import InfoBanner from 'components/banners/InfoBanner'
import Spinner from 'components/Spinner'
import useCurrentMerchant from 'hooks/useCurrentMerchant'
import useDispatch from 'hooks/useDispatch'
import useFlag from 'hooks/useFlag'
import { buildTranslate } from 'locales'
import React, { useEffect, useMemo, useState } from 'react'
import { Container } from 'react-bootstrap-five'
import Nav from 'react-bootstrap-five/Nav'
import Tab from 'react-bootstrap-five/Tab'
import Helmet from 'react-helmet'
import { push } from 'react-router-redux'
import { useIsMobile } from 'utilities/responsive'
import { OLO_ORDERING_PROVIDER } from '../compatibility'
import OloConfig from '../OloConfig'
import Overview from '../Overview'
import PosRedemptionBanner from '../PosRedemptionBanner'
import { hasGeneratingPool } from '../promotionPool'
import EmptyState from './EmptyState'
import PromotionsTable from './PromotionsTable'

enum Tabs {
  OVERVIEW = 'overview',
  ACTIVE = 'active',
  DRAFT = 'draft',
  COMPLETE = 'complete',
  OLO_CONFIG = 'olo_config',
}

const t = buildTranslate('promo_codes.landing')

const Landing = () => {
  const dispatch = useDispatch()
  const isMobile = useIsMobile()
  const merchant = useCurrentMerchant()
  const overviewEnabled = useFlag('mx-promo-codes-overview', false)
  const [activeTab, setActiveTab] = useState<Tabs>(
    overviewEnabled ? Tabs.OVERVIEW : Tabs.ACTIVE
  )

  // LaunchDarkly can resolve the flag after mount — FlagsProvider initializes
  // against `merchant-logged-out` and re-identifies once the merchant hydrates.
  // Without this the Overview tab appears in the bar but Active stays selected.
  useEffect(() => {
    if (overviewEnabled) setActiveTab(Tabs.OVERVIEW)
  }, [overviewEnabled])

  // Strict false check: `olo_promotions_enabled` is absent on older API
  // deploys, and undefined must not surface the activation flow to merchants
  // whose Olo promo codes already work.
  const needsOloActivation =
    merchant?.ordering_provider === OLO_ORDERING_PROVIDER &&
    merchant?.olo_promotions_enabled === false
  const showOloConfigTab = overviewEnabled && needsOloActivation

  // Channel-acceptance columns (LOY-622) ride the same flag as the Overview /
  // Olo Config tabs. The activate link is wired only when the Olo Config tab
  // exists (the sole in-app activation destination); otherwise "needs
  // activation" renders as plain text.
  const toastInstoreEnabled = useFlag('toast-promo-instore-beta', false)
  const onConfigureOnline = showOloConfigTab
    ? () => setActiveTab(Tabs.OLO_CONFIG)
    : undefined
  const channelColumnProps = {
    showChannels: overviewEnabled,
    merchant,
    toastInstoreEnabled,
    onConfigureOnline,
  }

  // Poll every 10s while any row in the list is still generating codes, so
  // the "Generating codes…" spinner flips to the final count without the
  // merchant having to refresh. Settles back to 0 (no polling) once every
  // pool is either complete or not generating. See Detail/index.tsx for
  // why this uses setState-during-render rather than useEffect.
  const [pollingInterval, setPollingInterval] = useState(0)
  const { data: { promotions = [] } = {}, isLoading } = useGetPromotionsQuery(
    undefined,
    { pollingInterval }
  )

  const anyPoolGenerating = useMemo(
    () => promotions.some(hasGeneratingPool),
    [promotions]
  )
  const nextPollingInterval = anyPoolGenerating ? 10000 : 0
  if (nextPollingInterval !== pollingInterval) {
    setPollingInterval(nextPollingInterval)
  }

  const onCreateClick = () => {
    dispatch(push('/promo_codes/new'))
  }

  const activePromotions = useMemo(
    () => promotions.filter(p => p.state === 'active'),
    [promotions]
  )
  const draftPromotions = useMemo(
    () => promotions.filter(p => p.state === 'draft'),
    [promotions]
  )
  const completePromotions = useMemo(
    () => promotions.filter(p => p.state === 'inactive'),
    [promotions]
  )

  // Only gate on `isLoading` (initial load) — `isFetching` flips true on
  // every background poll, and we don't want the spinner to replace the
  // rows every 10s. Polled data updates silently once received.
  const spinner = (
    <Spinner
      isLoading={isLoading}
      size="4x"
      className="d-flex justify-content-center pt-xl"
    />
  )

  const renderActiveContent = () => {
    if (activePromotions.length === 0) {
      return (
        <>
          <PosRedemptionBanner className="container pt-l" />
          <EmptyState />
        </>
      )
    }

    return (
      <Container className="pt-l">
        <Text.Header4 className="mb-xs">{t('active.title')}</Text.Header4>
        <Text.BodyText4 className="grey-70 mb-l">
          {t('active.description')}
        </Text.BodyText4>
        <PosRedemptionBanner className="mb-l" />
        <PromotionsTable
          promotions={activePromotions}
          isMobile={isMobile}
          tab="active"
          {...channelColumnProps}
        />
      </Container>
    )
  }

  const renderDraftContent = () =>
    draftPromotions.length > 0 ? (
      <Container className="pt-l">
        <PromotionsTable
          promotions={draftPromotions}
          isMobile={isMobile}
          tab="draft"
          {...channelColumnProps}
        />
      </Container>
    ) : (
      <div className="text-center py-xxl">
        <Text.BodyText4 className="grey-70">{t('draft.empty')}</Text.BodyText4>
      </div>
    )

  const renderCompleteContent = () =>
    completePromotions.length > 0 ? (
      <Container className="pt-l">
        <PromotionsTable
          promotions={completePromotions}
          isMobile={isMobile}
          tab="complete"
          {...channelColumnProps}
        />
      </Container>
    ) : (
      <div className="text-center py-xxl">
        <Text.BodyText4 className="grey-70">
          {t('complete.empty')}
        </Text.BodyText4>
      </div>
    )

  const isListTab = activeTab !== Tabs.OVERVIEW && activeTab !== Tabs.OLO_CONFIG

  return (
    <>
      <Helmet>
        <title>{t('page_title')}</title>
      </Helmet>
      <Tab.Container
        activeKey={activeTab}
        transition={false}
        onSelect={tab => setActiveTab(tab as Tabs)}
      >
        {/* White header area with title + tabs */}
        <Container>
          <div
            className={`d-flex py-xl ${
              isMobile
                ? 'flex-column align-items-center gap-l pt-xl pb-0'
                : 'justify-content-between align-items-center'
            }`}
          >
            <Text.Header2 className={isMobile ? 'mb-l' : 'm-0'}>
              {t('title')}
            </Text.Header2>
            <Button
              onClick={onCreateClick}
              data-testid="create-promo-code-button"
            >
              {t('create_promo_code')}
            </Button>
          </div>
        </Container>
        <div className="border-bottom-1 grey-20-border">
          <Nav className="container nav-tabs border-none">
            {overviewEnabled && (
              <Nav.Item>
                <Nav.Link eventKey={Tabs.OVERVIEW}>
                  {t('tabs.overview')}
                </Nav.Link>
              </Nav.Item>
            )}
            <Nav.Item>
              <Nav.Link eventKey={Tabs.ACTIVE}>{t('tabs.active')}</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey={Tabs.DRAFT}>{t('tabs.draft')}</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey={Tabs.COMPLETE}>{t('tabs.complete')}</Nav.Link>
            </Nav.Item>
            {showOloConfigTab && (
              <Nav.Item>
                <Nav.Link eventKey={Tabs.OLO_CONFIG}>
                  {t('tabs.olo_config')}
                </Nav.Link>
              </Nav.Item>
            )}
          </Nav>
        </div>

        {/* Gray content area */}
        <div className="grey-05-bkg pb-xxl promo-landing-content">
          {isListTab && (
            <Container className="pt-l">
              <InfoBanner showIcon text={t('info_banner')} />
            </Container>
          )}
          <Tab.Content>
            {/* Overview and Olo Config read only the merchant, so they render
                without waiting on the promotions query. */}
            {overviewEnabled && (
              <Tab.Pane eventKey={Tabs.OVERVIEW}>
                <Overview
                  onCreateClick={onCreateClick}
                  onGoToOloConfig={() => setActiveTab(Tabs.OLO_CONFIG)}
                />
              </Tab.Pane>
            )}
            {showOloConfigTab && (
              <Tab.Pane eventKey={Tabs.OLO_CONFIG}>
                <OloConfig />
              </Tab.Pane>
            )}
            <Switch
              condition={!isLoading}
              fallback={isListTab ? spinner : null}
            >
              <Tab.Pane eventKey={Tabs.ACTIVE}>
                {renderActiveContent()}
              </Tab.Pane>
              <Tab.Pane eventKey={Tabs.DRAFT}>{renderDraftContent()}</Tab.Pane>
              <Tab.Pane eventKey={Tabs.COMPLETE}>
                {renderCompleteContent()}
              </Tab.Pane>
            </Switch>
          </Tab.Content>
        </div>
      </Tab.Container>
    </>
  )
}

export default Landing
